import { AgreeToTermsPolicy, experience, ExtraParamsKey, SignInMode } from '@logto/schemas';
import { useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useSearchParams } from 'react-router-dom';

import LandingPageLayout from '@/Layout/LandingPageLayout';
import SingleSignOnFormModeContextProvider from '@/Providers/SingleSignOnFormModeContextProvider';
import SingleSignOnFormModeContext from '@/Providers/SingleSignOnFormModeContextProvider/SingleSignOnFormModeContext';
import WebAuthnContextProvider from '@/Providers/WebAuthnContextProvider';
import PasskeySignInButton from '@/components/Button/PasskeySignInButton';
import Divider from '@/components/Divider';
import GoogleOneTap from '@/components/GoogleOneTap';
import TextLink from '@/components/TextLink';
import EvaSignInCard from '@/components/eva/EvaSignInCard';
import SocialSignInList from '@/containers/SocialSignInList';
import TermsAndPrivacyCheckbox from '@/containers/TermsAndPrivacyCheckbox';
import TermsAndPrivacyLinks from '@/containers/TermsAndPrivacyLinks';
import useNativeSignIn from '@/hooks/use-native-sign-in';
import useNavigateWithPreservedSearchParams from '@/hooks/use-navigate-with-preserved-search-params';
import { useSieMethods } from '@/hooks/use-sie';
import useTerms from '@/hooks/use-terms';

import ErrorPage from '../ErrorPage';

import Main from './Main';
import styles from './index.module.scss';

const SignInFooters = () => {
  const { t } = useTranslation();
  const { termsValidation, agreeToTermsPolicy } = useTerms();
  const navigate = useNavigateWithPreservedSearchParams();

  const {
    signInMethods,
    signUpMethods,
    socialConnectors,
    signInMode,
    singleSignOnEnabled,
    passkeySignIn,
  } = useSieMethods();

  const { showSingleSignOnForm } = useContext(SingleSignOnFormModeContext);

  const handleSsoNavigation = useCallback(async () => {
    if (agreeToTermsPolicy === AgreeToTermsPolicy.Manual && !(await termsValidation())) {
      return;
    }
    navigate('/single-sign-on/email');
  }, [agreeToTermsPolicy, navigate, termsValidation]);

  if (showSingleSignOnForm) {
    return null;
  }

  return (
    <>
      {singleSignOnEnabled && (
        <>
          <div className={styles.singleSignOn}>
            {t('description.use')}{' '}
            <TextLink text="action.single_sign_on" onClick={handleSsoNavigation} />
          </div>
          {signInMethods.length === 0 &&
            socialConnectors.length === 0 &&
            agreeToTermsPolicy === AgreeToTermsPolicy.Manual && (
              <TermsAndPrivacyCheckbox className={styles.checkboxForSsoOnly} />
            )}
        </>
      )}
      {signInMode === SignInMode.SignInAndRegister && signUpMethods.length > 0 && (
        <div className={styles.createAccount}>
          {t('description.no_account')}{' '}
          <TextLink replace to="/register" text="action.create_account" />
        </div>
      )}
      {signInMethods.length > 0 && socialConnectors.length > 0 && (
        <>
          <Divider label="description.or" className={styles.divider} />
          <SocialSignInList socialConnectors={socialConnectors} className={styles.main} />
        </>
      )}
      {passkeySignIn?.enabled && passkeySignIn.showPasskeyButton && <PasskeySignInButton />}
    </>
  );
};

const DefaultSignIn = () => {
  const { signInMethods, socialConnectors } = useSieMethods();
  const { agreeToTermsPolicy } = useTerms();

  return (
    <LandingPageLayout title="description.sign_in_to_your_account">
      <GoogleOneTap context="signin" />
      <WebAuthnContextProvider>
        <SingleSignOnFormModeContextProvider>
          <Main signInMethods={signInMethods} socialConnectors={socialConnectors} />
          <SignInFooters />
        </SingleSignOnFormModeContextProvider>
      </WebAuthnContextProvider>
      {agreeToTermsPolicy !== AgreeToTermsPolicy.Manual && (
        <TermsAndPrivacyLinks className={styles.terms} />
      )}
    </LandingPageLayout>
  );
};

const EvaSignIn = () => {
  return (
    <div className={styles.sign} data-testid="eva-sign">
      <GoogleOneTap context="signin" />
      <EvaSignInCard />
    </div>
  );
};

const SignInContent = () => {
  const native = useNativeSignIn();

  return native ? <DefaultSignIn /> : <EvaSignIn />;
};

const SignIn = () => {
  const { signInMode } = useSieMethods();
  const [params] = useSearchParams();

  if (!signInMode) {
    return <ErrorPage />;
  }

  if (signInMode === SignInMode.Register) {
    return <Navigate to="/register" />;
  }

  if (params.get(ExtraParamsKey.OneTimeToken)) {
    return (
      <Navigate
        replace
        to={{ pathname: `/${experience.routes.oneTimeToken}`, search: `?${params.toString()}` }}
      />
    );
  }

  return <SignInContent />;
};

export default SignIn;
